<template>
  <main role="main">
    <error-notification />
    <section class="landing-banner">
      <header>
        <div class="banner-container">
          <router-link to="/" class="logo-link">
            <img class="logo" src="https://cidirectinvesting-assets.s3.ca-central-1.amazonaws.com/PPI-Valet-WealthBar-White.svg" :alt="brandName">
          </router-link>
          <div class="auth">
            <router-link id="signin" v-t to="/signin">Sign In</router-link>
            <router-link id="signup" v-t to="/signup" class="button">Create Account</router-link>
          </div>
        </div>
      </header>
      <div class="landing-content" aria-label="PPI sign up page">
        <h2 v-t>Elevate your practice into the digital age.</h2>
        <p v-t>Online wealth management to help you grow your business.</p>
        <a v-t href="#start-conversation" class="button neg">Grow your business.</a>
      </div>
    </section>

    <section class="sub-header">
      <div class="vertical-center-content">
        <div class="horizontal-center-content company-logos-container">
          <a href="http://www.ppi.ca" target="_blank" noreferrer noopener>
            <img
              class="ppi-logo"
              src="~@/assets/images/ppi_logo.png"
              :alt="$t('PPI logo')"
            >
          </a>
          <div class="divider-padding">
            <img
              class="divider"
              src="~@/assets/images/logo_divider.png"
              :alt="$t('vertical dividing line')"
            >
          </div>
          <a href="https://www.cidirectinvesting.com" target="_blank" noreferrer noopener>
            <img
              class="wb-logo"
              src="~@/assets/images/cidi_logo.png"
              :alt="$t('CI Direct Investing logo')"
            >
          </a>
        </div>
        <div class="horizontal-center-content">
          <p v-t class="page-text-width">
            PPI Valet is an innovative platform
            that helps advisors like you manage and grow the investment component of your business.
          </p>
        </div>
      </div>
    </section>
    <section class="laptop-container">
      <div class="panel-downward-graphic fluid-ratio-resize" />
      <img class="laptop-graphic" src="~@/assets/images/product_shot_laptop.png" :alt="$t('open laptop with valet dashboard')">
    </section>
    <section class="sign-up-reasons horizontal-center-content">
      <h3 v-t class="reason-header page-text-width">Added value for you and your clients.</h3>
      <p v-t class="page-text-width">
        You’ve spent years building your business and your clients value the relationship you share. You’d
        like to deepen those relationships while continuing to grow your business. PPI Valet can help.
      </p>
      <div class="reasons-list-container horizontal-center-content">
        <ul class="page-text-width">
          <li>
            <img src="~@/assets/images/checkbox.png" :alt="$t('checkbox')">
            <span v-t>
              <strong>Assured regulatory compliance</strong>, saving you time and giving you peace of mind
            </span>
          </li>
          <li>
            <img src="~@/assets/images/checkbox.png" :alt="$t('checkbox')">
            <span v-t>
              <strong>Portfolios exclusively for clients of advisors associated with PPI</strong>, that are professionally managed and rebalanced.
            </span>
          </li>
          <li>
            <img src="~@/assets/images/checkbox.png" :alt="$t('checkbox')">
            <span v-t>
              <strong>Low fees and the ability to set your own planning fee</strong>, so you can pass savings onto your clients
            </span>
          </li>
          <li>
            <img src="~@/assets/images/checkbox.png" :alt="$t('checkbox')">
            <span v-t>
              <strong>Digital onboarding, reducing paperwork</strong> and ensuring your clients get the portfolio that best aligns to their needs
            </span>
          </li>
        </ul>
      </div>
    </section>
    <section class="additional-information">
      <div class="image-links-container">
        <svg class="hide">
          <!-- Instantiate SVG -->
          <symbol id="right-arrow-svg">
            <polygon points="14.2,3.3 12.8,4.7 23.1,15 4,15 4,17 23.1,17 12.8,27.3 14.2,28.7 26.9,16   " />
          </symbol>
        </svg>
        <div class="image-container">
          <img class="image-link" src="~@/assets/images/read_overview.jpeg" :alt="$t('smiling woman with phone')">
        </div>
        <div class="image-container">
          <a href="/files/ppi/case-study.pdf" target="_blank" :aria-label="$t('Download advisor case study')" noreferrer noopener>
            <img class="image-link" src="~@/assets/images/advisor_case_study.jpeg" :alt="$t('business man looking outside a high rise')">
          </a>
          <a href="/files/ppi/case-study.pdf" target="_blank" noreferrer noopener>
            <div class="link-container">
              <p v-t>See an advisory case study</p>
              <svg viewBox="0 0 32 32">
                <use xlink:href="#right-arrow-svg" />
              </svg>
            </div>
          </a>
        </div>
        <div class="image-container">
          <a href="http://help.ppivalet.ca/portfolio-performance" target="_blank" :aria-label="$t('Check out Valet portfolio performance')" noreferrer noopener>
            <img class="image-link" src="~@/assets/images/portfolio.jpeg" :alt="$t('computer screen showing financial reports')">
          </a>
          <a href="http://help.ppivalet.ca/portfolio-performance" target="_blank" noreferrer noopener>
            <div class="link-container">
              <p v-t>Check out the Valet portfolios</p>
              <svg viewBox="0 0 32 32">
                <use xlink:href="#right-arrow-svg" />
              </svg>
            </div>
          </a>
        </div>
      </div>
    </section>

    <section id="start-conversation" class="start-conversation">
      <div class="conversation-container">
        <h3 v-t class="center">Ready to start a conversation?</h3>
        <p v-t class="center">
          Take the next step towards scaling your business and helping clients. Complete the form below
          and a PPI Valet representative will be in touch.
        </p>
        <vue-form id="valetInquiry" :state="formState" @submit.prevent="submitForm">
          <div class="inline-input full">
            <validate class="field">
              <label v-t for="firstName">First Name</label>
              <input
                id="firstName"
                v-model.trim="firstName"
                type="text"
                name="firstName"
                maxlength="64"
                :class="{ invalid: inputError.firstName.$invalid }"
                containsonlylanguagechars
                required
                @blur="updateError('firstName')"
              >
              <div v-if="inputError.firstName.$error" class="error-message">
                <div v-if="inputError.firstName.$error.required" v-t>Required</div>
                <div v-if="inputError.firstName.$error.containsonlylanguagechars" v-t>Must contain only letters, hyphens, or dots</div>
              </div>
            </validate>

            <validate class="field">
              <label v-t for="lastName">Last Name</label>
              <input
                id="lastName"
                v-model.trim="lastName"
                type="text"
                name="lastName"
                maxlength="64"
                :class="{ invalid: inputError.lastName.$invalid }"
                containsonlylanguagechars
                required
                @blur="updateError('lastName')"
              >
              <div v-if="inputError.lastName.$error" class="error-message">
                <div v-if="inputError.lastName.$error.required" v-t>Required</div>
                <div v-if="inputError.lastName.$error.containsonlylanguagechars" v-t>Must contain only letters, hyphens, or dots</div>
              </div>
            </validate>
          </div>

          <validate class="field">
            <label v-t for="email">Email</label>
            <input
              id="email"
              v-model.trim="email"
              type="email"
              name="email"
              maxlength="64"
              :class="{ invalid: inputError.email.$invalid }"
              required
              @blur="updateError('email')"
            >
            <div v-if="inputError.email.$error" class="error-message">
              <div v-if="inputError.email.$error.required" v-t>Required</div>
              <div v-if="inputError.email.$error.email" v-t>Invalid email</div>
            </div>
          </validate>

          <validate class="field">
            <label v-t for="phone">Phone</label>
            <input
              id="phone"
              v-model.trim="phone"
              type="tel"
              name="phone"
              maxlength="40"
              :class="{ invalid: inputError.phone.$invalid }"
              loosephonevalidation
              required
              @blur="updateError('phone')"
            >
            <div v-if="inputError.phone.$error" class="error-message">
              <div v-if="inputError.phone.$error.required" v-t>Required</div>
              <div v-if="inputError.phone.$error.loosephonevalidation" v-t>Must contain only numbers or ( ) - + .</div>
            </div>
          </validate>

          <div v-if="serverErrorMessage">
            <p class="error-message">{{ serverErrorMessage }}</p>
          </div>

          <button v-t type="button" class="full" @click.prevent="submitForm">Submit</button>
        </vue-form>
        <p class="center">
          <span v-t>
            Contact one of our regional valet representatives directly.
          </span>
          &nbsp;
          <router-link v-t to="/contact">Contact us</router-link>
        </p>
      </div>
    </section>
    <modal
      :title="contactModalTitle"
      size="small"
      :displayed="displayed"
      @close="closeModal"
    >
      <section slot="body">
        <p v-t class="modal-text">A PPI Valet Representative will be in touch shortly.</p>
      </section>
    </modal>

    <public-footer />
  </main>
</template>

<script>
import { http } from '@/lib/api/rest';
import { errorNotification, modal, publicFooter } from '@/components/layout';
import getBrandConfig from '@/lib/utils/get_brand_config';
import errorStore from '@/store/error';

const brandConfig = getBrandConfig();
const _ = {
  brochureSite: brandConfig.brochureSite,
  http,
  errorStore,
};

export default {
  _,
  components: {
    errorNotification,
    modal,
    publicFooter,
  },

  data() {
    return {
      brandName: 'PPI Valet',
      formState: {},
      inputError: {
        firstName: {},
        lastName: {},
        email: {},
        phone: {},
        server: {},
      },
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      displayed: false,
      serverErrorMessage: '',
    };
  },

  head() {
    return {
      title: this.$t('PPI Valet'),
    };
  },

  async beforeRouteEnter(to, from, next) {
    if (_.brochureSite) {
      next();
    } else { next('/signin'); } // Skip brochure page, go straight to signin
  },

  computed: {
    contactModalTitle() {
      return this.$t('Thank you!');
    },
  },

  methods: {
    updateError(fieldName) {
      this.inputError[fieldName] = Object.assign({}, this.formState[fieldName]);
      this.inputError[fieldName].$error = Object.assign({}, this.formState[fieldName].$error);
    },

    updateAllErrors() {
      this.updateError('firstName');
      this.updateError('lastName');
      this.updateError('email');
      this.updateError('phone');
    },

    buildClientInfo() {
      return Object.assign({
        name: `${this.firstName} ${this.lastName}`,
        email: this.email,
        phone: this.phone,
      });
    },

    clearFormState() {
      this.firstName = '';
      this.lastName = '';
      this.email = '';
      this.phone = '';
    },

    showModal() {
      this.displayed = true;
    },

    closeModal() {
      this.displayed = false;
    },

    displayServerError(error) {
      const response = error.response;
      if ('statusText' in response) {
        _.errorStore.addError();
      } else {
        this.serverErrorMessage = response.data;
      }
    },

    sendUserData(url, payload) {
      return http.post(url, payload);
    },

    async submitForm() {
      if (this.formState.$invalid) {
        this.updateAllErrors();
      } else {
        try {
          await this.sendUserData('/advisor_leads', this.buildClientInfo());
          this.showModal();
          this.serverErrorMessage = '';
          this.clearFormState();
        } catch (error) {
          this.displayServerError(error);
        }
      }
    },
  },
};

</script>

<style scoped lang="scss">


  $soft-shadow: 0 2px 4px 0 rgba(51, 51, 51, 0.2);

  .banner-container {
    max-width: 1280px;
    margin: 0 auto;
    padding: 0.5rem 0.75rem;
    display: flex;
    flex-direction: row;
    align-items: center;
    height: 3rem;
    min-height: 3rem;
    width: 100%;
  }

  .logo-link {
    padding: 0.25rem 0 0;
    img { height: 4rem; }

    @media #{$screen-width-medium} {
      padding: 1.5rem 0 0;
      img { height: 7rem; }
    }
  }

  .auth {
    flex: 1 0 auto;
    text-align: right;

    #signin {
      color: white;
      padding: 0.75rem;
      text-decoration: none;
    }
  }

  main {
    background-color: white;

    input:active, input:focus, textarea:active, textarea:focus { background-color: $white !important; }

    .hide { display: none; }

    .horizontal-center-content {
      display: flex;
      flex-shrink: 0;
      justify-content: center;
      align-items: center;
    }

    .page-text-width {
      max-width: 600px;
      width: 85vw;
    }
  }

  .landing-banner {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    background-color: black;
    background-image: url('~@/assets/images/hero-mobile.jpg');
    background-position: center top 3rem;
    background-repeat: no-repeat;
    background-size: auto 50vh;
    color: $white;
    height: 90vh;
    @media #{$screen-width-medium} {
      height: auto;
      background-image: url('~@/assets/images/hero.jpg');
      background-position: center center;
      background-size: cover;
    }

    a.cta-btn {
      display: inline-block;
      background: linear-gradient(180deg, #fd8f74 0%, #e96654 100%);
      text-decoration: none;
      color: #fff;
      padding: 5px 18px;
      border-radius: 0.25rem;
      box-shadow: $soft-shadow;
    }

    .cta-btn:hover { background: linear-gradient(180deg, #bd776a 0%, #b95a49 100%); }


  }

  .landing-content {
    flex: 1 0 auto;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    align-items: flex-start;
    padding: 2rem 1rem;
    color: $white;

    @media #{$screen-width-medium} {
      position: relative;
      left: 50%;
      width: 50%;
      max-width: 26rem;
      padding: 4rem 3rem 12rem;
      text-align: left;

      p, a.button { margin-top: 1.5rem; }
    }
  }

  .sub-header {
    background-color: #f2f4f4;
    margin-bottom: 4rem;

    .vertical-center-content {
      display: flex;
      flex-direction: column;
      justify-content: space-evenly;
      padding: 60px 0;

      .company-logos-container {
        margin-bottom: 30px;
        padding: 0 40px;
        @media #{$screen-width-medium} { padding: 0 100px; }
      }

      .ppi-logo {
        width: 100%;
        max-width: 113px;
      }

      .divider-padding { padding: 0 5vw; }

      .divider {
        width: 100%;
        max-width: 4px;
      }

      .wb-logo {
        width: 100%;
        max-width: 193px;
      }

      p { text-align: center; }
    }
  }

  .laptop-container {
    position: relative;

    .panel-downward-graphic {
      position: relative;
      margin: 40px 0;
      @media #{$screen-max-small} { margin: 20px 0 0; }
    }

    .laptop-graphic {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translateX(-50%) translateY(-50%);
      min-width: 180px;
      max-width: 100vw;
      max-height: 100%;
    }
  }

  .fluid-ratio-resize {
    background-size: cover;
    background-position: center;
    max-width: 100vw;
    *height: 150px;
  }

  .fluid-ratio-resize::after {
    content: " ";
    display: block;
    height: 102.786px;
    width: 100%;
    padding-top: 4.918%;
  }

  .sign-up-reasons {
    display: flex;
    flex-direction: column;
    max-width: 60em;
    margin: 0 auto;
    text-align: center;
    width: 100%;

    .reason-header { margin: 3rem 0; }

    .reasons-list-container {
      margin-top: 1rem;

      ul {
        li {
          margin-top: 2rem;
          display: flex;
          align-items: flex-start;

          img {
            margin-top: 3px;
            max-width: 1rem;
            margin-right: 1rem;
          }

          span { text-align: left; }
        }
      }

      @media #{$screen-width-medium} {
        li { margin-top: 3rem; }
      }
    }
  }

  .additional-information {
    margin: 3rem 0;
    text-align: center;

    @media #{$screen-width-medium} { margin: 4rem 0; }

    h3 {
      margin: 0 auto;
      max-width: 80%;
      @media #{$screen-max-small} { margin: 1rem auto; }
    }

    .panel-upward-graphic {
      padding: 60px 0;
      background-position: center;
      background-repeat: no-repeat;
      background-size: 100vw 10rem;

      .video-container {
        height: 42vw;
        max-height: 20rem;
        margin: auto;
      }
    }

    .image-links-container {
      display: flex;
      flex-direction: column;
      flex-wrap: nowrap;
      justify-content: space-around;
      max-width: 1480px;
      margin: 0 auto;

      @media #{$screen-width-medium} {
        flex-direction: row;
        margin-top: 4rem;
      }

      .image-container {
        margin-top: 2rem;
        @media #{$screen-width-medium} { margin-top: 0; }

        .link-container {
          display: flex;
          flex-direction: row;
          justify-content: center;
          margin-top: 2rem;
          @media #{$screen-width-medium} { margin-top: 3rem; }

          svg {
            width: 1rem;
            height: 1.4rem;
            margin-left: 1rem;
          }
        }

        a:hover + a {
          color: $primary-300;
          fill: $primary-300;
        }

        .link-container:hover:nth-child(1) {
          color: $primary-300;
          fill: $primary-300;
        }

        .image-link {
          display: block;
          position: relative;
          margin: 0 auto;
          width: 75vw;
          min-width: 180px;
          max-width: 311px;
          box-shadow: 4px 4px 15px 0 rgba(0, 0, 0, 0.2);
          @media #{$screen-width-medium} {
            width: 26vw;
            min-width: 260px;
          }
        }

        a {
          display: block;
          text-decoration: none;
          color: $black;
        }
      }
    }
  }

  .start-conversation {
    width: 100%;
    background: linear-gradient(#f7f9f9, #fff);
    padding: 3rem 2rem;
    margin: 0 auto;
    @media #{$screen-width-medium} {
      padding: 4rem 0;
      span { margin-top: 0.5rem; }
    }

    .conversation-container {
      margin: 0 auto;
      max-width: 500px;
      a {
        @media #{$screen-max-small} { display: block; }
      }
    }

    #valetInquiry { margin: 3rem 0; }
  }

  .modal-text {
    padding: 2rem 2rem 0;

    @media #{$screen-width-small} {
      margin: 2rem auto;
      padding: 0;
      max-width: 20rem;
    }
  }

</style>
